/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Label, MultilineLabel } from 'src/modules/labeling/types/Label';
import { Rect } from 'src/modules/labeling/types/Rect';
import {
    MULTI_LINE_MARGIN,
    SHAPE_LEVEL_HEIGHT,
    SHAPE_LEVEL_HEIGHT_NO_NAME,
    tokenContainerDataAttribute,
    tokenDataAttribute,
    tokenIndexDataAttribute
} from 'src/modules/labeling/utils/labelingConstants';

export type Line = [number, number];

const isMultilineLabel = (label: Label): label is MultilineLabel => (<MultilineLabel>label).hasBorderStart !== undefined;

/**
 * Receives labels and lines array and converts each label to multiline label(s).
 */
export const convertToMultiline = (labels: (MultilineLabel | Label)[], lines: Line[]): MultilineLabel[][] => {
    const multilineLabels: MultilineLabel[][] = lines.map(() => []);

    labels.forEach(label => {
        const { start, end } = label;

        for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
            const [lineStart, lineEnd] = lines[lineIndex];
            if (start >= lineStart && end <= lineEnd) {
                multilineLabels[lineIndex].push({
                    // If it's an already multiline label, then preserve its
                    // border start, end and original label.
                    ...(isMultilineLabel(label)
                        ? { ...label }
                        : { ...label, hasBorderEnd: true, hasBorderStart: true, originalLabel: label }),
                    line: lineIndex
                });
                break;
            } else if (start >= lineStart && start <= lineEnd) {
                multilineLabels[lineIndex].push({
                    // If it's an already multiline label, then preserve its border start and original label.
                    ...(isMultilineLabel(label) ? { ...label } : { ...label, originalLabel: label, hasBorderStart: true }),
                    start,
                    end: lineEnd,
                    hasBorderEnd: false,
                    line: lineIndex
                });
            } else if (end >= lineStart && end <= lineEnd) {
                multilineLabels[lineIndex].push({
                    // If it's an already multiline label, then preserve its original label.
                    ...(isMultilineLabel(label) ? { ...label } : { ...label, originalLabel: label, hasBorderEnd: true }),
                    start: lineStart,
                    end,
                    hasBorderStart: false,
                    line: lineIndex
                });
            } else if (start < lineStart && end > lineEnd) {
                multilineLabels[lineIndex].push({
                    // If it's an already multiline label, then preserve its original label.
                    ...(isMultilineLabel(label) ? { ...label } : { ...label, originalLabel: label }),
                    start: lineStart,
                    end: lineEnd,
                    hasBorderEnd: false,
                    hasBorderStart: false,
                    line: lineIndex
                });
            }
        }
    });

    return multilineLabels;
};

/**
 * Given token rects, calculates and returns line indices.
 */
export const calculateLineIndices = (rects: Rect[], absoluteStartIndex: number): Line[] => {
    const lines: Line[] = [];
    let lineStartIndex = 0;

    for (let index = 0; index < rects.length; index++) {
        const rect = rects[index];
        const nextRect = rects[index + 1] && rects[index + 1];

        if (!nextRect) {
            lines.push([lineStartIndex + absoluteStartIndex, index + absoluteStartIndex]);
            break;
        }

        if (rect.top !== nextRect.top) {
            lines.push([lineStartIndex + absoluteStartIndex, index + absoluteStartIndex]);

            lineStartIndex = index + 1;
        }
    }

    return lines;
};

export type CalcMaxLevel = {
    /**
     * Array of labels.
     */
    labels: Label[];
};

export type CalcMaxLevelReturn = { upperMostLevel: number; lowerMostLevel: number };

/**
 * Calculates the height for each line.
 */
export const calcLinesHeights = ({
    lineIndices,
    multilineLabels
}: {
    lineIndices: Line[];
    multilineLabels: MultilineLabel[];
}): CalcMaxLevelReturn[] =>
    lineIndices.map(([lineStart, lineEnd]) =>
        multilineLabels
            .filter(({ start, end }) => start >= lineStart && end <= lineEnd)
            .reduce(
                (acc, shape) => {
                    if (shape.renderedAs === 'box') {
                        return { ...acc, upperMostLevel: Math.max(acc.upperMostLevel, shape.renderingLevel) };
                    }
                    return { ...acc, lowerMostLevel: Math.max(acc.lowerMostLevel, shape.renderingLevel) };
                },
                { upperMostLevel: 0, lowerMostLevel: 0 }
            )
    );

/**
 * Calculates token's vertical margin.
 */
export const calcTokenVerticalMargin = (level: number, hideNames: boolean) => {
    if (level === 0) {
        return MULTI_LINE_MARGIN;
    }

    if (level === 1) {
        return MULTI_LINE_MARGIN * 1.5;
    }

    return level * (hideNames ? SHAPE_LEVEL_HEIGHT_NO_NAME : SHAPE_LEVEL_HEIGHT);
};

/**
 * Get the target token using the givin attributes selector.
 */
export const getTooltipTargetToken = (
    containerId: string,
    tokensAttachedIndex: number
) => `[${tokenContainerDataAttribute}="${containerId}"]
    [${tokenIndexDataAttribute}="${tokensAttachedIndex}"]
    [${tokenDataAttribute}="true"]`;

export const getTokenElementByIndex = (ref: HTMLElement, index: number) =>
    ref.querySelector(`[${tokenIndexDataAttribute}="${index}"]> [${tokenDataAttribute}]`);

export const getFirstTokenElement = (ref: HTMLElement) => ref.querySelector(`[${tokenDataAttribute}]`);
